import {
  PylonBrowserTracker,
  type EventPayload,
  type PaymentPayload,
  type SendResult,
} from '@hyperconnect/pylon-browser-tracker';
import * as Sentry from '@sentry/nextjs';
import { v4 } from 'uuid';

import appConfig from 'app-config';

import { DEBUG } from 'src/config';
import store from 'src/stores';
import { userDataAtom } from 'src/stores/auth/atoms';
import { debugEventLogsAtom } from 'src/stores/debug/atom';
import { TUTMParams, UTMParamsAtom } from 'src/stores/utm/atoms';
import { visitorConfigAtom } from 'src/stores/visitor/atoms';
import { EVENT_TYPE, EventName, EventOption, EventType } from 'src/types/Event';
import getDeviceId from 'src/utils/device/id';
import getDeviceInfo, { isPwaMode } from 'src/utils/device/info';
import { initializeMetric } from 'src/utils/metric';
import { safeWindow } from 'src/utils/static-generation';

const service_code = appConfig.serviceCode.toUpperCase();

interface EventMessage {
  event_type: EventType;
  event_name: EventName | string;
  event_timestamp: number;
  event_uid: string;
  device_params: {
    os: 'WEB_VIEW' | 'WEB';
  };
  /**
   * 앱 클라이언트의 app_params(api_version, version_code, version_name)에 대응.
   * 웹은 클라이언트 레벨에서 별도의 version_code를 관리하지 않아 api_version, version_name만 포함한다.
   */
  app_params: {
    api_version?: string | number;
    version_name?: string;
  };
  user_id: string | null;
  event_params: unknown;
  service_code: string;
  unique_user_key: string;
  utm_params: TUTMParams;
}

export type EventData<T = object> = {
  eventType: EventMessage['event_type'];
  eventName: EventMessage['event_name'];
  eventParams?: T;
  options?: EventOption;
  /** GTM dataLayer로도 보낼지 여부. 기본 true — 이미 다른 경로로 dataLayer에 보낸 이벤트를
   * 이벤트로그에도 적재하고 싶을 때만 false로 넘겨 중복 전송을 피한다. */
  sendToDataLayer?: boolean;
};

type EventGatewayType = 'PAYMENT' | 'GENERAL';

const EVENT_GATEWAY_SEND_INTERVAL = 3_000;

/** EventMessage의 `user_id: string | null`, `event_params: unknown`은 SDK 계약
 * (`user_id?: string`, `event_params?: Record<string, unknown>`)과 구조적으로
 * 다르므로, 전송 직전에만 명시적으로 좁혀서 변환한다. */
function toEventPayload(message: EventMessage): EventPayload {
  return {
    ...message,
    user_id: message.user_id ?? undefined,
    event_params: message.event_params as Record<string, unknown>,
  };
}

function toPaymentPayload(message: EventMessage): PaymentPayload {
  return toEventPayload(message);
}

/** Payment 관련 이벤트는 전용 엔드포인트로 전송해야 함.
 * @see {@link https://www.notion.so/hpcnt/API-8b5dbd04bc2f4601863a40abb0c5e460?pvs=4#32cd2c7091014968983c9cf43bfc2185}
 */
const EVENT_ENDPOINT_MAP: Record<EventGatewayType, '/' | `/${string}/`> = {
  PAYMENT: '/event-gateway/api/v1/event/payment/',
  GENERAL: '/event-gateway/api/v1/event/',
};

class EventGatewayClient {
  private static instance: EventGatewayClient;

  private origin: string | null = null;

  private eventMessageQueue: Record<EventGatewayType, EventMessage[]> = {
    GENERAL: [],
    PAYMENT: [],
  };

  private sendIntervalId: number | null = null;

  private _start() {
    this.sendIntervalId = window.setInterval(() => {
      this.sendEventMessages('GENERAL');
      this.sendEventMessages('PAYMENT');
    }, EVENT_GATEWAY_SEND_INTERVAL);
    window.addEventListener('beforeunload', () => {
      if (this.sendIntervalId) window.clearInterval(this.sendIntervalId);
      this.sendEventMessages('GENERAL', { keepalive: true });
      this.sendEventMessages('PAYMENT', { keepalive: true });
    });

    // iOS Safari 등 beforeunload가 발생하지 않는 환경에서도 이벤트 전송을 보장하기 위해
    // visibilitychange (hidden) 시점에도 큐를 flush한다.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        // microtask로 지연하여 동일 visibilitychange 이벤트를 구독하는 다른 리스너들이
        // 동기적으로 큐에 이벤트를 push한 뒤 flush되도록 보장한다.
        queueMicrotask(() => {
          this.sendEventMessages('GENERAL', { keepalive: true });
          this.sendEventMessages('PAYMENT', { keepalive: true });
        });
      }
    });
  }

  static getInstance(): EventGatewayClient {
    if (!EventGatewayClient.instance) {
      EventGatewayClient.instance = new EventGatewayClient();
    }

    return EventGatewayClient.instance;
  }

  public initialize(origin: string) {
    try {
      new URL(origin);
    } catch {
      throw new Error('event gateway origin is not valid.');
    }

    this.origin = origin;

    this._start();

    this.sendEventMessages('GENERAL');
    this.sendEventMessages('PAYMENT');
  }

  public pushEvent({ eventName, eventType, eventParams, options }: EventData) {
    const deviceInfo = getDeviceInfo();
    const { browserName, browserVersion } = deviceInfo;

    if (browserName === 'safari' && browserVersion === '14.0.3') {
      //https://hpcnt.slack.com/archives/C02PLFTA5DL/p1666321514312989
      return;
    }

    const eventMessage = {
      event_type: eventType,
      event_name: eventName,
      event_timestamp: new Date().getTime(),
      event_uid: v4(),
      device_params: {
        os: typeof window !== 'undefined' && window.AzarJs ? 'WEB_VIEW' : 'WEB',
      },
      app_params: {
        // 웹뷰는 네이티브 브릿지가 실제 서버 통신에 사용하는 api_version을 알려주고,
        // 순수 웹은 API 요청 시 고정으로 보내는 값(appConfig.headers['x-azar-api-version'])을 그대로 사용
        api_version:
          safeWindow?.AzarJs?.getApiVersion?.() ?? appConfig.headers?.['x-azar-api-version'],
        version_name: process.env.NEXT_PUBLIC_RELEASE,
      },
      user_id: store.get(userDataAtom)?.userId || null,
      event_params: {
        path: window.location.pathname,
        unique_user_key: getDeviceId(),
        ...eventParams,
        ...deviceInfo,
      },
      service_code,
      unique_user_key: getDeviceId(),
      utm_params: { ...store.get(UTMParamsAtom) },
      ...options,
    } satisfies EventMessage;

    if (eventType === EVENT_TYPE.PAYMENT) {
      this.eventMessageQueue.PAYMENT.push(eventMessage);
    } else {
      this.eventMessageQueue.GENERAL.push(eventMessage);
    }
  }

  private async sendEventMessages(
    eventGatewayType: EventGatewayType,
    options?: { keepalive?: boolean }
  ) {
    if (this.origin == null) return;

    const eventMessages = this.eventMessageQueue[eventGatewayType].splice(0, 100);

    if (!eventMessages.length) {
      return;
    }

    const requeueFailedMessages = (failType: 'not_ok' | 'error') => {
      const retryMessages: EventMessage[] = eventMessages.map((message) => ({
        ...message,
        event_params: {
          ...(message.event_params as Record<string, unknown>),
          event_gateway_send_fail_type: failType,
        },
      }));

      this.eventMessageQueue[eventGatewayType] = retryMessages.concat(
        this.eventMessageQueue[eventGatewayType]
      );
    };

    try {
      const sdk = PylonBrowserTracker.getInstance();
      const sdkOptions = { keepalive: options?.keepalive, retries: 0 };
      const result: SendResult =
        eventGatewayType === 'PAYMENT'
          ? await sdk.sendPaymentEventBatch(eventMessages.map(toPaymentPayload), sdkOptions)
          : await sdk.sendEventBatch(eventMessages.map(toEventPayload), sdkOptions);
      if (!result.success) {
        if (result.status !== 0 && result.error == null) {
          requeueFailedMessages('not_ok');
          Sentry.captureMessage('Event Gateway Response Not OK', {
            extra: {
              status: result.status,
            },
          });
        } else {
          requeueFailedMessages('error');
          Sentry.captureMessage('Event Gateway Send Failed', {
            extra: {
              status: result.status,
              error: result.error,
            },
          });
        }
      }
    } catch (error) {
      requeueFailedMessages('error');
      Sentry.captureMessage('Event Gateway Send Failed', {
        extra: {
          error,
        },
      });
    }
  }
}

export const pushEvent = (eventData: EventData) => {
  const { eventName, eventType, options, eventParams, sendToDataLayer = true } = eventData;
  const isPwa = isPwaMode();
  const { isMobile, os } = getDeviceInfo();
  const ABConfig = {
    ...(store.get(visitorConfigAtom) || {}),
  };

  const commonEventParams = {
    ...(isPwa ? { isPwa } : {}),
    ABConfig,
  };
  const eventParamsWithConfigs = {
    ...eventParams,
    ...commonEventParams,
  };

  const endpoint = EVENT_ENDPOINT_MAP[eventType === EVENT_TYPE.PAYMENT ? 'PAYMENT' : 'GENERAL'];

  if (sendToDataLayer) {
    window.dataLayer?.push({
      event: eventName,
      eventType,
      eventParams: {
        ...eventParamsWithConfigs,
        device_id: getDeviceId(),
        os,
        isMobile,
      },
      options: { ...options, device_id: getDeviceId() },
      url: endpoint,
    });
  }

  if (DEBUG) {
    store.set(
      debugEventLogsAtom,
      [{ ...eventData, date: new Date().toISOString() }]
        .concat(store.get(debugEventLogsAtom))
        .slice(0, 100)
    );
  }

  EventGatewayClient.getInstance().pushEvent({ ...eventData, eventParams: eventParamsWithConfigs });
};

//NOTE: 이벤트/메트릭 전송에 필요한 부트스트랩을 순서대로 실행합니다.
/**
 * 1. `PylonBrowserTracker`(이벤트·메트릭이 공유하는 전송 SDK 싱글턴)를 우선 초기화 해야합니다.
 *    (이 호출이 먼저 끝나야 아래 두 초기화가 실제로 전송을 성공시킬 수 있기때문)
 *    (초기화 전에 전송을 시도해도 throw하지 않고 조용히 실패만 하므로, 순서가
 *    바뀌어도 에러로 드러나지 않습니다. — 순서를 바꾸지 않아야합니다.)
 * 2. 이후 `EventGatewayClient`를 초기화해 이벤트 큐 flush 타이머를 시작합니다.
 * 3. `metric` 모듈에 대기 중이던 메트릭 콜백(예: sendDeviceInfo)이 있다면 flush합니다.
 */
export const setEventGatewayOrigin = (origin: string) => {
  PylonBrowserTracker.getInstance().initialize({ origin, serviceCode: service_code });
  EventGatewayClient.getInstance().initialize(origin);
  initializeMetric(origin);
};
